<template>
  <div class="institution-module">
    <b-row class="d-flex justify-content-around">
      <b-col
        lg="10"
        md="10"
        cols="12"
        class="content-section"
      >
        <!-- Loading State -->
        <div
          v-if="isLoading"
          class="loading-container"
        >
          <b-spinner
            variant="primary"
            class="loading-spinner"
          />
          <p>Loading chat history...</p>
        </div>

        <!-- Chat Container -->
        <div
          v-else-if="chat"
          class="chat-container"
        >
          <div class="chat-header">
            <div class="user-info">
              <div class="avatar">
                {{ getUserInitials() }}
              </div>
              <div class="user-details">
                <h4>{{ getUserFullName() }}</h4>
                <p>{{ chat.institution ? chat.institution.name : chat.user.email }}</p>
              </div>
            </div>
            <div class="experiment-info">
              <h3>{{ chat.experiment.name }}</h3>
            </div>
          </div>

          <div class="chat-history">
            <div
              v-for="(message, index) in visibleMessages"
              :key="message.id"
              class="message-wrapper"
            >
              <!-- User Message -->
              <div class="message user-message">
                <div class="message-avatar">
                  <div class="user-avatar">
                    {{ getUserInitials() }}
                  </div>
                </div>
                <div class="message-content">
                  <div
                    class="message-text"
                    v-html="formatMessage(message.question)"
                  />
                  <div class="message-time">
                    {{ formatDate(message.created_at) }}
                  </div>
                </div>
              </div>

              <!-- AI Response -->
              <div
                v-if="message.answer"
                class="message ai-message"
              >
                <div class="message-avatar">
                  <div class="ai-avatar">
                    <img
                      width="24"
                      height="24"
                      src="@/assets/images/skeleton/AI Icon.svg"
                      alt=""
                    >
                  </div>
                </div>
                <div class="message-content">
                  <div
                    class="message-text"
                    v-html="formatMessage(message.answer)"
                  />
                  <div class="message-time">
                    {{ formatDate(message.updated_at) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-else
          class="empty-state"
        >
          <div class="empty-icon">
            💬
          </div>
          <h3>No Chat History Found</h3>
          <p>Unable to load the chat conversation.</p>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  BRow,
  BCol,
  BProgress,
  BCard,
  BButton,
  BIcon,
  BImg,
  BCardText,
  BSpinner,
  BAvatar,
  BLink,
} from 'bootstrap-vue'

export default {
  name: 'ConcreteInstitutionPage',
  components: {
    BRow,
    BCol,
    BProgress,
    BButton,
    BIcon,
    BImg,
    BCard,
    BCardText,
    BSpinner,
    BAvatar,
    BLink,
  },
  computed: {
    ...mapGetters({
      chat: 'chatbot/item',
    }),
    id() {
      return this.$route.params.id ? this.$route.params.id : null
    },
    visibleMessages() {
      if (!this.chat || !this.chat.messages) return []
      return this.chat.messages.filter((message, index) => {
        // Hide the first message if it is the context/initial prompt
        if (index === 0 && !message.answer) {
          return false
        }
        return true
      })
    },
  },
  data() {
    return {
      studentDetails: [],
      isLoading: false,
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.isLoading = true
      this.$store.dispatch('chatbot/get', this.id).then(response => {
        console.log('Response in details vue: ', response)
        console.log('Chat data: ', this.chat)
        this.isLoading = false
      }).catch(error => {
        console.error('Error loading chat:', error)
        this.isLoading = false
      })
    },
    getUserFullName() {
      if (this.chat && this.chat.user) {
        return `${this.chat.user.fname} ${this.chat.user.lname}`
      }
      return 'User'
    },
    getUserInitials() {
      if (this.chat && this.chat.user) {
        return `${this.chat.user.fname?.charAt(0) || ''}${this.chat.user.lname?.charAt(0) || ''}`
      }
      return 'U'
    },
    // formatMessage(text) {
    //   if (!text) return "";

    //   // Convert line breaks to HTML
    //   let formattedText = text.replace(/\n/g, '<br>');

    //   // Format numbered lists
    //   formattedText = formattedText.replace(/(\d+\.)\s/g, '<br><span class="list-number">$1</span> ');

    //   // Format bold text (text between **)
    //   formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    //   return formattedText;
    // },
    formatMessage(text) {
      if (!text) return ''

      let formattedText = text.replace(/\n/g, '<br>')
      formattedText = formattedText.replace(/(\d+\.)\s/g, '<br><span class="list-number">$1</span> ')
      formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')

      // More comprehensive URL pattern
      // const urlPattern = /(https?:\/\/[^\s<]+|www\.[^\s<]+\.[^\s<]+|(?:\w+\.)+\w+(?:\/[^\s<]*)?)/gi;
      const urlPattern = /(https?:\/\/[^\s<()]+|www\.[^\s<()]+\.[^\s<()]+)(?=\)|,|\.|\?|!)?/gi
      formattedText = formattedText.replace(urlPattern, url => {
        // Check if it's a valid URL format
        let href = url
        if (!href.startsWith('http://') && !href.startsWith('https://')) {
          href = `http://${href}`
        }

        // Basic URL validation
        if (href.includes('.') && href.length > 4) {
          return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-primary" style="text-decoration: underline;">${url}</a>`
        }

        return url
      })

      return formattedText
    },
    formatDate(dateString) {
      if (!dateString) return ''
      try {
        const date = new Date(dateString)
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      } catch (error) {
        return ''
      }
    },
  },
}
</script>

<style scoped>
.chat-container {
  background: rgba(6, 14, 30, 0.8);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.chat-header {
  padding: 24px;
  background: rgba(13, 50, 77, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #860dcc 0%, #0fd9de 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 18px;
  color: white;
}

.user-details h4 {
  margin: 0;
  color: white;
  font-weight: 600;
}

.user-details p {
  margin: 4px 0 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
}

.experiment-info h3 {
  margin: 0 0 8px;
  color: white;
  font-weight: 600;
}

.experiment-desc {
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  max-width: 400px;
  line-height: 1.4;
}

.chat-history {
  padding: 24px;
  max-height: 70vh;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(133, 102, 242, 0.5) transparent;
}

.chat-history::-webkit-scrollbar {
  width: 6px;
}

.chat-history::-webkit-scrollbar-track {
  background: transparent;
}

.chat-history::-webkit-scrollbar-thumb {
  background: rgba(133, 102, 242, 0.5);
  border-radius: 3px;
}

.message-wrapper {
  margin-bottom: 32px;
}

.message {
  display: flex;
  margin-bottom: 16px;
  gap: 12px;
}

.user-message {
  flex-direction: row-reverse;
}

.message-avatar {
  flex-shrink: 0;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #860dcc 0%, #0fd9de 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 14px;
  color: white;
}

.ai-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(15, 217, 222, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0fd9de;
  border: 1px solid rgba(15, 217, 222, 0.3);
}

.message-content {
  max-width: 70%;
  display: flex;
  flex-direction: column;
}

.user-message .message-content {
  align-items: flex-end;
}

.ai-message .message-content {
  align-items: flex-start;
}

.message-text {
  padding: 16px;
  border-radius: 18px;
  line-height: 1.5;
  color: white;
  position: relative;
  word-wrap: break-word;
}

.user-message .message-text {
  background: linear-gradient(135deg, #860dcc 0%, #0fd9de 100%);
  border-bottom-right-radius: 4px;
}

.ai-message .message-text {
  background: rgba(29, 19, 63, 0.6);
  border: 1px solid rgba(133, 102, 242, 0.2);
  border-bottom-left-radius: 4px;
}

.message-time {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 6px;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: white;
}

.loading-spinner {
  width: 3rem;
  height: 3rem;
  margin-bottom: 1rem;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: white;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.7;
}

.empty-state h3 {
  margin-bottom: 0.5rem;
  color: white;
}

.empty-state p {
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
}

/* Text formatting styles */
.message-text strong {
  color: #0fd9de;
}

.list-number {
  display: inline-block;
  width: 24px;
  color: #0fd9de;
  font-weight: 600;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .chat-header {
    flex-direction: column;
  }

  .message-content {
    max-width: 85%;
  }

  .experiment-desc {
    max-width: 100%;
  }
}

@media (max-width: 576px) {
  .chat-header {
    padding: 16px;
  }

  .chat-history {
    padding: 16px;
  }

  .message-content {
    max-width: 90%;
  }
}
</style>
